import { redirect } from '@tanstack/react-router';
import { hasOrgFeature, type CustomerOrganizationType, type OrgFeature } from '@siliconx/shared';
import { fetchClientIdentity } from './auth';

/**
 * SIL-1102/SIL-1108: shared `beforeLoad` gate for a business-only route
 * section — call `requireOrgFeature('someFeature')` from a section layout's
 * (or business-only leaf route's) `beforeLoad`. Extracted from SIL-1102's
 * `/market-intelligence` fix so every business-only section shares one
 * implementation instead of copying it per route.
 *
 * WHY beforeLoad, not the root `OrgFeatureGate` (__root.tsx) alone:
 * `OrgFeatureGate` redirects in a `useEffect` that only fires once
 * `useAuth()`'s identity query resolves — it runs strictly AFTER TanStack
 * Router's `beforeLoad`/loader chain and after child components mount. A
 * direct hit on a business-only URL therefore let the route's own
 * `beforeLoad`/loader (or an unconditional mount-time fetch) fire its
 * business-only API calls — 403 in console — before the root gate's
 * redirect ever landed. Putting the SAME check in the section's own
 * `beforeLoad` runs before any child route's `beforeLoad`/loader/component,
 * short-circuiting the whole subtree the instant a redirect throws
 * (TanStack Router never runs a child route's `beforeLoad` once a parent's
 * `beforeLoad` redirects).
 *
 * Semantics (matches SIL-1102 exactly):
 * - identity fetch failure -> do NOT redirect (fail open here; the root
 *   `OrgFeatureGate` re-checks once identity resolves through its own
 *   query, so a genuinely denied org still gets redirected).
 * - `organizationType` resolved but `null` -> redirect (fail closed,
 *   `hasOrgFeature(null, feature)` is `false` by construction).
 * - `fetchClientIdentity()` is the same memoized identity fetch
 *   `AuthProvider` (authContext.tsx) uses — calling it here reuses its
 *   in-flight/cached promise rather than firing a second `/me` request.
 */
export async function requireOrgFeature(feature: OrgFeature): Promise<void> {
  let organizationType: CustomerOrganizationType | null;
  try {
    organizationType = (await fetchClientIdentity()).organizationType;
  } catch {
    return;
  }
  if (!hasOrgFeature(organizationType, feature)) {
    throw redirect({ to: '/dashboard' });
  }
}
